import { View, Text, Pressable, Platform, useWindowDimensions } from "react-native";
import { Slot, Link, usePathname } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useState } from "react";
import { GUIDE_PAGES } from "./_components";

/** Sidebar navigation item */
const SidebarItem = ({ href, label, icon, color, isActive }: {
  href: string;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  color: string;
  isActive: boolean;
}) => (
  <Link href={href as any} asChild>
    <Pressable
      className={`flex-row items-center gap-2 px-3 py-2 rounded-lg transition-colors ${isActive ? "bg-violet-100" : "hover:bg-gray-50"}`}
      style={{ cursor: "pointer" } as any}
    >
      <Ionicons name={icon} size={15} color={isActive ? color : "#9CA3AF"} />
      <Text
        className={`text-xs ${isActive ? "font-semibold" : "font-medium"}`}
        style={{ color: isActive ? "#1F2937" : "#6B7280" }}
      >
        {label}
      </Text>
    </Pressable>
  </Link>
);

/**
 * Guide section layout with sidebar navigation on desktop,
 * collapsible menu on mobile.
 */
export default function GuideLayout() {
  const pathname = usePathname();
  const { width } = useWindowDimensions();
  const [menuOpen, setMenuOpen] = useState(false);
  const isDesktop = width >= 1024;
  const isIndex = pathname === "/guide" || pathname === "/(web)/guide";
  const isFullPage = pathname.includes("/guide/full");

  if (Platform.OS !== "web") return null;

  // Full page (PDF) has no sidebar/chrome
  if (isFullPage) {
    return <Slot />;
  }

  return (
    <View className="flex-1 min-h-screen bg-white">
      {/* Top header bar */}
      <View className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4">
        <View className="max-w-6xl mx-auto w-full flex-row items-center justify-between">
          <View className="flex-row items-center gap-3">
            <Link href="/(web)" asChild>
              <Pressable className="flex-row items-center gap-2" style={{ cursor: "pointer" } as any}>
                <Ionicons name="arrow-back" size={20} color="#7C3AED" />
                <Text className="text-sm font-medium text-violet-700">Home</Text>
              </Pressable>
            </Link>
            <View className="w-px h-5 bg-gray-200" />
            <Text className="text-lg font-bold text-gray-900">Handleiding</Text>
          </View>

          <View className="flex-row items-center gap-3">
            <Link href="/(web)/guide/full" asChild>
              <Pressable
                className="flex-row items-center gap-2 px-4 py-2 rounded-lg bg-violet-50 hover:bg-violet-100 transition-colors"
                style={{ cursor: "pointer" } as any}
              >
                <Ionicons name="download-outline" size={16} color="#7C3AED" />
                <Text className="text-sm font-medium text-violet-700">PDF</Text>
              </Pressable>
            </Link>

            {/* Mobile menu toggle */}
            {!isDesktop && (
              <Pressable
                onPress={() => setMenuOpen(!menuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100"
                style={{ cursor: "pointer" } as any}
              >
                <Ionicons name={menuOpen ? "close" : "menu"} size={24} color="#374151" />
              </Pressable>
            )}
          </View>
        </View>
      </View>

      {/* Mobile navigation dropdown */}
      {!isDesktop && menuOpen && (
        <View className="bg-white border-b border-gray-200 px-4 py-3">
          <View className="max-w-6xl mx-auto w-full gap-1">
            <Link href="/(web)/guide" asChild>
              <Pressable
                onPress={() => setMenuOpen(false)}
                className={`px-4 py-3 rounded-xl ${isIndex ? "bg-violet-100" : ""}`}
                style={{ cursor: "pointer" } as any}
              >
                <Text className={`text-sm ${isIndex ? "font-semibold text-gray-900" : "text-gray-600"}`}>
                  Overzicht
                </Text>
              </Pressable>
            </Link>
            {GUIDE_PAGES.map((page) => {
              const isActive = pathname.includes(page.href.replace("/(web)", ""));
              return (
                <Pressable key={page.href} onPress={() => setMenuOpen(false)}>
                  <SidebarItem {...page} isActive={isActive} />
                </Pressable>
              );
            })}
          </View>
        </View>
      )}

      {/* Main content area — use CSS overflow for proper web scrolling */}
      <View
        className="flex-1 flex-row max-w-7xl mx-auto w-full"
        style={{ overflow: "hidden" } as any}
      >
        {/* Desktop sidebar */}
        {isDesktop && (
          <View
            className="border-r border-gray-100 py-4 px-2"
            style={{
              width: 180,
              overflowY: "auto",
              height: "calc(100vh - 57px)",
              position: "sticky",
              top: 0,
              flexShrink: 0,
            } as any}
          >
            <Link href="/(web)/guide" asChild>
              <Pressable
                className={`flex-row items-center gap-2 px-3 py-2 rounded-lg mb-1 ${isIndex ? "bg-violet-100" : "hover:bg-gray-50"}`}
                style={{ cursor: "pointer" } as any}
              >
                <Ionicons name="book" size={15} color={isIndex ? "#7C3AED" : "#9CA3AF"} />
                <Text className={`text-xs ${isIndex ? "font-semibold text-gray-900" : "font-medium text-gray-500"}`}>
                  Overzicht
                </Text>
              </Pressable>
            </Link>

            <View className="h-px bg-gray-100 mx-3 my-1.5" />

            {GUIDE_PAGES.map((page) => {
              const isActive = pathname.includes(page.href.replace("/(web)", ""));
              return <SidebarItem key={page.href} {...page} isActive={isActive} />;
            })}
          </View>
        )}

        {/* Page content — scrollable via CSS overflow */}
        <View
          className="flex-1 py-8 px-6 lg:px-16"
          style={{
            overflowY: "auto",
            height: "calc(100vh - 57px)",
          } as any}
        >
          <View style={{ maxWidth: 800 } as any}>
            <Slot />
            <View className="h-12" />
          </View>
        </View>
      </View>
    </View>
  );
}
